import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Form,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { TableColumnsType } from 'antd';
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError } from '../../api/errors';
import {
  AuthorizationSubjectType,
  createDataScopeRule,
  deleteDataScopeRule,
  getRole,
} from '../../api/roles';
import type { DataScopeRule, RoleDetail } from '../../api/roles';
import {
  availableResourceNameOptions,
  dataScopeLabel,
  dataScopeOptions,
  isDataScopeImplemented,
} from './dataScopeDisplay';

/** Tên tài nguyên nghĩa là mọi tài nguyên. Khớp với hằng số cùng tên ở máy chủ. */
const allResources = '*';

/** Giá trị của biểu mẫu lập quy tắc phạm vi. */
interface ScopeFormValues {
  readonly resourceName: string;
  readonly scopeType: number;
  readonly priority: number;
}

/** Tham số của hộp quản lý quy tắc phạm vi dữ liệu. */
export interface RoleDataScopeModalProps {
  /** Vai trò đang quản lý quy tắc phạm vi, hoặc rỗng để đóng hộp. */
  readonly role: RoleDetail | null;

  /** Người đang đăng nhập có quyền lập và xóa quy tắc phạm vi. */
  readonly canManage: boolean;

  /** Nhận vai trò sau khi quy tắc thay đổi. */
  readonly onChanged: (updated: RoleDetail) => void;

  /** Đóng hộp. */
  readonly onClose: () => void;
}

/**
 * Hộp lập và xóa quy tắc phạm vi dữ liệu của một vai trò.
 *
 * **Đây là lớp phân quyền quyết định người dùng THẤY được những dòng nào**, khác với quyền vốn quyết
 * định họ LÀM được những gì. Thiếu quy tắc phạm vi thì màn hình hiện ra nhưng danh sách rỗng — và đó
 * là cấu hình sai dễ mắc nhất khi lập vai trò mới.
 *
 * Hai quyết định của hộp này:
 *
 * 1. **Chặn hẳn những phạm vi chưa cài đặt** thay vì chỉ ghi chú. Một phạm vi chưa cài đặt vẫn lưu
 *    được vào cơ sở dữ liệu nhưng không có tác dụng gì, và hệ quả là người giữ vai trò đó không thấy
 *    dòng nào mà không ai đoán được nguyên nhân. Xem `dataScopeDisplay` để biết phạm vi nào đã chạy.
 * 2. **Cảnh báo trước khi xóa quy tắc cuối cùng.** Xóa nó làm người giữ vai trò mất sạch dữ liệu nhìn
 *    thấy, và điều đó không hiện ra ở đâu ngoài lời than của họ.
 *
 * Mỗi vai trò chỉ có **một** quy tắc cho mỗi tài nguyên, nên ô chọn tài nguyên chỉ liệt kê những tài
 * nguyên chưa có quy tắc. Vai trò hệ thống thì không thêm, không xóa được quy tắc — máy chủ chặn.
 */
export function RoleDataScopeModal({
  role,
  canManage,
  onChanged,
  onClose,
}: RoleDataScopeModalProps): ReactNode {
  const [form] = Form.useForm<ScopeFormValues>();

  const [isBusy, setIsBusy] = useState(false);

  const isOpen = role !== null;
  const rules = role?.dataScopeRules ?? [];

  /*
    Quy tắc phạm vi của vai trò hệ thống được dựng sẵn cùng hệ thống và máy chủ chặn sửa, xóa. Ẩn hẳn
    các nút thay vì hiện những nút chắc chắn thất bại.
  */
  const isSystemRole = role?.role.isSystemRole ?? false;
  const canEditRules = canManage && !isSystemRole;

  /*
    Loại tài nguyên đã có quy tắc: máy chủ từ chối quy tắc thứ hai cho cùng một tài nguyên, kể cả khi
    khác loại phạm vi. Để người quản trị chọn rồi mới nhận lỗi thì phí một lượt nhập.
  */
  const resourceOptions = useMemo(
    () =>
      availableResourceNameOptions(
        (role?.dataScopeRules ?? []).map((rule) => rule.resourceName),
      ),
    [role],
  );

  /*
    Chỉ cho chọn những phạm vi đã cài đặt thật. Những phạm vi còn lại vẫn hiện trong bảng nếu đã có
    quy tắc dùng chúng — người quản trị cần thấy để xóa — nhưng không lập mới được.
  */
  const selectableScopeOptions = useMemo(
    () =>
      dataScopeOptions
        .filter((option) => option.isImplemented)
        .map((option) => ({ value: option.value, label: option.label })),
    [],
  );

  /** Giải thích của phạm vi đang chọn, hiện ngay dưới ô chọn. */
  const [selectedScopeType, setSelectedScopeType] = useState<number | null>(null);

  const selectedScopeDescription = useMemo(() => {
    if (selectedScopeType === null) {
      return 'Phạm vi quyết định người giữ vai trò này thấy được những dòng nào.';
    }

    return (
      dataScopeOptions.find((option) => option.value === selectedScopeType)?.description
      ?? 'Phạm vi quyết định người giữ vai trò này thấy được những dòng nào.'
    );
  }, [selectedScopeType]);

  async function handleCreate(values: ScopeFormValues): Promise<void> {
    if (role === null) {
      return;
    }

    setIsBusy(true);

    try {
      await createDataScopeRule({
        subjectType: AuthorizationSubjectType.Role,
        subjectId: role.role.id,
        resourceName: values.resourceName,
        scopeType: values.scopeType,
        specificDepartmentId: null,
        priority: values.priority,
      });

      void message.success('Đã lập quy tắc phạm vi dữ liệu.');
      form.resetFields();
      setSelectedScopeType(null);

      // Máy chủ trả về chính quy tắc vừa lập, không trả về cả vai trò. Nạp lại vai trò để bảng quy tắc
      // và các con số ở danh sách bên ngoài cùng cập nhật.
      onChanged(await getRole(role.role.id));
    } catch (error) {
      void message.error(
        error instanceof ApiError ? error.message : 'Không lập được quy tắc phạm vi.',
      );
    } finally {
      setIsBusy(false);
    }
  }

  function confirmDelete(rule: DataScopeRule): void {
    if (role === null) {
      return;
    }

    const isLastRule = rules.length === 1;
    const hasAllResourcesRule = rules.some((candidate) => candidate.resourceName === allResources);

    /*
      Hậu quả của việc xóa phụ thuộc vào quy tắc "*": nó áp cho mọi tài nguyên, còn quy tắc của tài
      nguyên cụ thể cộng thêm vào nó. Xóa quy tắc của một tài nguyên khi KHÔNG có quy tắc "*" là chặn
      hết tài nguyên đó — câu cũ "chỉ thu hẹp chứ không chặn hết" sai trong trường hợp này.
    */
    let content: string;

    if (isLastRule) {
      content =
        'Đây là quy tắc phạm vi CUỐI CÙNG của vai trò. Xóa nó làm mọi người đang giữ vai trò này '
        + 'không thấy dòng dữ liệu nào — họ mở màn hình ra và thấy danh sách rỗng.';
    } else if (rule.resourceName === allResources) {
      content =
        'Quy tắc "*" áp cho mọi tài nguyên. Xóa nó thì người giữ vai trò chỉ còn thấy những tài '
        + 'nguyên có quy tắc riêng; các tài nguyên khác hiện danh sách rỗng.';
    } else if (hasAllResourcesRule) {
      content =
        'Vai trò còn quy tắc "*", nên người giữ vai trò vẫn thấy tài nguyên này theo quy tắc đó — '
        + 'chỉ mất phần rộng thêm mà quy tắc này cấp.';
    } else {
      content =
        'Vai trò không có quy tắc "*", nên xóa quy tắc này làm người giữ vai trò không thấy dòng '
        + 'nào của tài nguyên này.';
    }

    Modal.confirm({
      title: 'Xóa quy tắc phạm vi này?',
      content,
      okText: 'Xóa',
      okButtonProps: { danger: true },
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await deleteDataScopeRule(rule.id);

          void message.success('Đã xóa quy tắc phạm vi.');
          onChanged(await getRole(role.role.id));
        } catch (error) {
          void message.error(
            error instanceof ApiError ? error.message : 'Không xóa được quy tắc phạm vi.',
          );
        }
      },
    });
  }

  const columns: TableColumnsType<DataScopeRule> = [
    {
      title: 'Tài nguyên',
      dataIndex: 'resourceName',
      width: 200,
      render: (resourceName: string) => (
        <Typography.Text strong>
          {resourceName === '*' ? 'Mọi tài nguyên (*)' : resourceName}
        </Typography.Text>
      ),
    },
    {
      title: 'Phạm vi',
      dataIndex: 'scopeType',
      render: (scopeType: number) => (
        <Space size={4} wrap>
          <span>{dataScopeLabel(scopeType)}</span>

          {!isDataScopeImplemented(scopeType) && (
            <Tag color="red">Chưa cài đặt — không có tác dụng</Tag>
          )}
        </Space>
      ),
    },
    {
      title: '',
      key: 'unrestricted',
      width: 170,
      render: (_value: unknown, rule: DataScopeRule) =>
        rule.isUnrestrictedWithinCompany ? <Tag color="green">Toàn pháp nhân</Tag> : null,
    },
    { title: 'Thứ tự', dataIndex: 'priority', width: 90 },
    {
      title: '',
      key: 'actions',
      width: 110,
      render: (_value: unknown, rule: DataScopeRule) =>
        canEditRules ? (
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              confirmDelete(rule);
            }}
          >
            Xóa
          </Button>
        ) : null,
    },
  ];

  return (
    <Modal
      title={
        role === null
          ? 'Phạm vi dữ liệu'
          : `Phạm vi dữ liệu của vai trò ${role.role.code}`
      }
      open={isOpen}
      width={860}
      footer={<Button onClick={onClose}>Đóng</Button>}
      onCancel={onClose}
      destroyOnHidden
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {rules.length === 0 ? (
          <Alert
            type="warning"
            showIcon
            message="Vai trò này chưa có quy tắc phạm vi nào"
            description={
              'Người giữ vai trò này KHÔNG thấy dòng dữ liệu nào — họ mở màn hình ra và thấy danh '
              + 'sách rỗng, kể cả khi đã được cấp đủ quyền. Đây là cấu hình sai dễ mắc nhất khi lập '
              + 'vai trò mới.'
            }
          />
        ) : (
          <Alert
            type="info"
            showIcon
            message="Rộng nhất thắng"
            description={
              'Phạm vi lấy hợp của mọi quy tắc — một người kiêm hai vai trò thì thấy phần dữ liệu '
              + 'rộng nhất trong số đó. Điều này ngược với quyền theo cột, nơi hạn chế nhất thắng.'
            }
          />
        )}

        <Table<DataScopeRule>
          rowKey="id"
          size="small"
          columns={columns}
          dataSource={[...rules]}
          pagination={false}
          locale={{ emptyText: 'Chưa có quy tắc phạm vi nào.' }}
        />

        {isSystemRole && (
          <Typography.Text type="secondary">
            Phạm vi dữ liệu của vai trò hệ thống do hệ thống quản lý: quy tắc được dựng sẵn và không
            thêm, sửa hay xóa được ở đây.
          </Typography.Text>
        )}

        {canEditRules && resourceOptions.length === 0 && (
          <Typography.Text type="secondary">
            Vai trò đã có quy tắc cho mọi tài nguyên. Mỗi tài nguyên chỉ có một quy tắc — muốn đổi
            loại phạm vi của một tài nguyên thì xóa quy tắc cũ rồi lập lại.
          </Typography.Text>
        )}

        {canEditRules && resourceOptions.length > 0 && (
          <Form<ScopeFormValues>
            form={form}
            layout="vertical"
            requiredMark={false}
            disabled={isBusy}
            initialValues={{ priority: 0 }}
            onFinish={(values) => {
              void handleCreate(values);
            }}
          >
            <Form.Item
              name="resourceName"
              label="Tài nguyên áp dụng"
              extra={
                'Chỉ liệt kê những tài nguyên mà máy chủ thật sự tra phạm vi và vai trò chưa có quy '
                + 'tắc — mỗi tài nguyên chỉ một quy tắc. Dấu sao áp cho mọi tài nguyên; muốn rộng hơn '
                + 'cho một tài nguyên thì giữ quy tắc "*" và lập thêm quy tắc cho tài nguyên đó, hai '
                + 'quy tắc cộng vào nhau.'
              }
              rules={[{ required: true, message: 'Chưa chọn tài nguyên.' }]}
            >
              <Select<string> options={[...resourceOptions]} />
            </Form.Item>

            <Form.Item
              name="scopeType"
              label="Loại phạm vi"
              extra={selectedScopeDescription}
              rules={[{ required: true, message: 'Chưa chọn loại phạm vi.' }]}
            >
              <Select<number>
                options={selectableScopeOptions}
                onChange={(value) => {
                  setSelectedScopeType(value);
                }}
              />
            </Form.Item>

            <Form.Item
              name="priority"
              label="Thứ tự hiển thị"
              extra="Chỉ để sắp xếp danh sách. KHÔNG quyết định quy tắc nào thắng — phạm vi luôn lấy hợp của mọi quy tắc."
              rules={[{ required: true, message: 'Chưa nhập thứ tự.' }]}
            >
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={isBusy}>
                Lập quy tắc phạm vi
              </Button>
            </Form.Item>
          </Form>
        )}
      </Space>
    </Modal>
  );
}
